import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, of } from 'rxjs';

import { APP_VERSION } from '../constants/app-version.constant';

interface VersionResponse {
    version: string;
}
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private static readonly VERSION_FILE_URL = 'assets/version.json';
  private static readonly STORAGE_KEY = 'appVersion';
  private static readonly LAST_PROMPTED_KEY = 'lastPromptedVersion';

  private readonly updateSubject = new BehaviorSubject<string | null>(null);
  private versionCheckTimerId: number | null = null;

  private latestRemoteVersion: string | null = null; // guarda a última detectada

  constructor(private readonly httpClient: HttpClient) {}

  get updateAvailable$() {
    return this.updateSubject.asObservable();
  }

  initVersionCheck(intervalInMs = 5 * 60 * 1000): void {
    this.checkForUpdate();
    this.versionCheckTimerId = window.setInterval(() => this.checkForUpdate(), intervalInMs);
  }

  stopVersionCheck(): void {
    if (this.versionCheckTimerId !== null) {
      window.clearInterval(this.versionCheckTimerId);
      this.versionCheckTimerId = null;
    }
  }

  async applyUpdate(): Promise<void> {
    // garante que o SW não sirva o bundle antigo
    await this.unregisterServiceWorkers();
    await this.clearCaches();
    // força navegar à raiz para evitar algum roteamento cacheado
    window.location.replace(`${location.origin}/?v=${Date.now()}`);
  }

  private checkForUpdate(): void {
    this.httpClient
      .get<VersionResponse>(`${VersionCheckService.VERSION_FILE_URL}?cb=${Date.now()}`, {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      })
      .pipe(
        map((response) => response?.version?.trim()),
        catchError(() => of(APP_VERSION))
      )
      .subscribe((remoteVersion) => {
        if (!remoteVersion) return;

        this.ensureStoredVersion();

        // baseline: use a versão armazenada (ou APP_VERSION se não houver)
        const storedVersion = localStorage.getItem(VersionCheckService.STORAGE_KEY) || APP_VERSION;

        if (this.isNewerVersion(remoteVersion, storedVersion)) {
          this.latestRemoteVersion = remoteVersion;

          // marque que já avisamos o usuário sobre esta versão
          localStorage.setItem(VersionCheckService.LAST_PROMPTED_KEY, remoteVersion);

          // também atualize o STORAGE_KEY para evitar o "loop" após reload
          localStorage.setItem(VersionCheckService.STORAGE_KEY, remoteVersion);

          this.updateSubject.next(remoteVersion);
          this.stopVersionCheck();
        } else {
          // sem update: mantenha STORAGE_KEY coerente com o bundle atual
          this.updateSubject.next(null);
          localStorage.setItem(VersionCheckService.STORAGE_KEY, storedVersion || APP_VERSION);
        }
      });
  }

  private ensureStoredVersion(): void {
    if (!localStorage.getItem(VersionCheckService.STORAGE_KEY)) {
      localStorage.setItem(VersionCheckService.STORAGE_KEY, APP_VERSION);
    }
  }

  private async clearCaches(): Promise<void> {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch (error) {
      console.warn('[VersionCheckService] Não foi possível limpar o cache de service workers.', error);
    }

    try {
      // Preserve a versão já conhecida para evitar o loop ao voltar
      const storedVersion = localStorage.getItem(VersionCheckService.STORAGE_KEY);
      const lastPrompted = localStorage.getItem(VersionCheckService.LAST_PROMPTED_KEY);
      localStorage.clear();
      if (storedVersion) localStorage.setItem(VersionCheckService.STORAGE_KEY, storedVersion);
      if (lastPrompted) localStorage.setItem(VersionCheckService.LAST_PROMPTED_KEY, lastPrompted);
    } catch (error) {
      console.warn('[VersionCheckService] Não foi possível limpar o localStorage.', error);
    }

    try {
      sessionStorage.clear();
    } catch (error) {
      console.warn('[VersionCheckService] Não foi possível limpar o sessionStorage.', error);
    }
  }

  private async unregisterServiceWorkers(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (error) {
      console.warn('[VersionCheckService] Falha ao desregistrar Service Workers.', error);
    }
  }

  private isNewerVersion(remoteVersion: string, currentVersion: string): boolean {
    const remoteSegments = this.normaliseVersion(remoteVersion);
    const currentSegments = this.normaliseVersion(currentVersion);
    const maxLength = Math.max(remoteSegments.length, currentSegments.length);

    for (let index = 0; index < maxLength; index++) {
      const r = remoteSegments[index] ?? 0;
      const c = currentSegments[index] ?? 0;
      if (r > c) return true;
      if (r < c) return false;
    }
    return false;
  }

  private normaliseVersion(version: string): number[] {
    return version
      .split('.')
      .map((segment) => Number(segment))
      .filter((segment) => !Number.isNaN(segment));
  }
}
